import React, { useState, useEffect } from "react";
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import apiDefinitions from "../../../../../core/api/apiDefinitions";
import { ApiResponse } from "./VehicleView";
import { showSuccessAlert, showItemExistsError, showError } from '../../../../../core/alert/sweetAlertConfig';

interface EmissionTest {
  id: number;
  numberPlate: string;
  ownerName: string | null;
  ownerAddress: string | null;
  vehicleClass: string | null;
  fuelType: string | null;
  grossWeight: number;
  noOfKms: number;
  serialNumber: string | null;
  issueDate: string | null;
  center: string | null;
  testFee: number;
  overallStatus: string | null;
  expiryDate: string | null;
  status: string | null;
  images: string[];
  createdBy: string | null;
  createdAt: string | null;
}

interface FormErrors {
  vehicleClass?: string;
  serialNumber?: string;
  fuelType?: string;
  noOfKms?: string;
  issueDate?: string;
  center?: string;
  testFee?: string;
  overallStatus?: string;
  expiryDate?: string;
  images?: string;
}

interface VehicleEmissionTestProps {
  show: boolean;
  onHide: () => void;
  numberPlate: string;
  vehicleClass: string | null;
  fuelType: string | null;
}

const VehicleEmissionTest: React.FC<VehicleEmissionTestProps> = ({ show, onHide, numberPlate, vehicleClass, fuelType }) => {
  const [emissionTest, setEmissionTest] = useState<EmissionTest | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
  const [history, setHistory] = useState<EmissionTest[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    vehicleClass: vehicleClass || "",
    serialNumber: "",
    fuelType: fuelType || "",
    noOfKms: "",
    issueDate: "",
    center: "",
    testFee: "",
    overallStatus: "",
    expiryDate: "",
  });
  const [files, setFiles] = useState<File[]>([]);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [emissionImagePreviews, setEmissionImagePreviews] = useState<string[]>([]);
  const maxFileSizeMB = 5;
  const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleString("en-US", {
        timeZone: "Asia/Colombo",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "Invalid Date";
    }
  };

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  const handleAddModalOpen = () => {
    setShowAddModal(true);
    setFormErrors({});
    setFormData({
      vehicleClass: vehicleClass || "",
      serialNumber: "",
      fuelType: fuelType || "",
      noOfKms: "",
      issueDate: "",
      center: "",
      testFee: "",
      overallStatus: "",
      expiryDate: "",
    });
    setFiles([]);
    setEmissionImagePreviews([]);
  };

  const handleAddModalClose = () => {
    setShowAddModal(false);
    setFormErrors({});
    emissionImagePreviews.forEach(URL.revokeObjectURL);
    setEmissionImagePreviews([]);
  };

  const handleHistoryModalOpen = async () => {
    setShowHistoryModal(true);
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const response = await apiDefinitions.getEmissionTestHistory(numberPlate);
      const data: ApiResponse<EmissionTest[]> = response.data;
      if (data.status === "S1000" && data.data) {
        setHistory(data.data);
      } else {
        setHistoryError(data.statusDescription || `Failed to fetch emission test history (Status: ${data.status})`);
        showError(data.statusDescription || `Failed to fetch emission test history (Status: ${data.status})`);
        setHistory([]);
      }
    } catch (err: any) {
      const message = err.response?.data?.statusDescription || err.message || "Error fetching emission test history";
      setHistoryError(message);
      showError(message);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleHistoryModalClose = () => {
    setShowHistoryModal(false);
    setHistory([]);
    setHistoryError(null);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFiles: React.Dispatch<React.SetStateAction<File[]>>,
    maxSizeMB: number,
    allowedTypes: string[]
  ) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      const validFiles: File[] = [];
      const errors: FormErrors = {};

      selectedFiles.forEach((file) => {
        if (!allowedTypes.includes(file.type)) {
          errors.images = `Invalid file type for ${file.name}. Allowed types: JPEG, PNG, JPG.`;
          return;
        }
        if (file.size > maxSizeMB * 1024 * 1024) {
          errors.images = `File ${file.name} exceeds ${maxSizeMB}MB limit.`;
          return;
        }
        validFiles.push(file);
      });

      setFiles(validFiles);
      setFormErrors((prev) => ({ ...prev, images: errors.images || '' }));
      generatePreviews(validFiles, setEmissionImagePreviews);
    }
  };

  const generatePreviews = (files: File[], setPreviews: React.Dispatch<React.SetStateAction<string[]>>) => {
    const previews = files.map((file) => URL.createObjectURL(file));
    setPreviews((prev) => {
      prev.forEach(URL.revokeObjectURL);
      return previews;
    });
  };

  const removeFile = (
    index: number,
    files: File[],
    setFiles: React.Dispatch<React.SetStateAction<File[]>>,
    previews: string[],
    setPreviews: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    const updatedPreviews = previews.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    setPreviews(updatedPreviews);
    updatedPreviews.forEach(URL.revokeObjectURL);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: FormErrors = {};

    // Existing field validations
    if (!formData.vehicleClass) errors.vehicleClass = "Vehicle Class is required.";
    if (!formData.serialNumber) errors.serialNumber = "Serial Number is required.";
    if (!formData.fuelType) errors.fuelType = "Fuel Type is required.";
    if (!formData.noOfKms) errors.noOfKms = "Number of KMs is required.";
    if (!formData.issueDate) errors.issueDate = "Issue Date is required.";
    if (!formData.center) errors.center = "Center is required.";
    if (!formData.testFee) errors.testFee = "Test Fee is required.";
    if (!formData.overallStatus) errors.overallStatus = "Overall Status is required.";
    if (!formData.expiryDate) errors.expiryDate = "Expiry Date is required.";

    // Date validations
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to start of day for comparison
    const issueDate = formData.issueDate ? new Date(formData.issueDate) : null;
    const expiryDate = formData.expiryDate ? new Date(formData.expiryDate) : null;

    if (issueDate) {
      if (issueDate > today) {
        errors.issueDate = "Issue Date cannot be in the future.";
      }
      if (expiryDate && issueDate > expiryDate) {
        errors.issueDate = "Issue Date must be before or equal to Expiry Date.";
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setFormErrors({});
      const payload = {
        numberPlate,
        vehicleClass: formData.vehicleClass,
        serialNumber: formData.serialNumber,
        fuelType: formData.fuelType,
        noOfKms: parseInt(formData.noOfKms, 10),
        issueDate: formData.issueDate,
        center: formData.center,
        testFee: parseFloat(formData.testFee),
        overallStatus: formData.overallStatus,
        expiryDate: formData.expiryDate,
      };
      const formDataToSend = new FormData();
      formDataToSend.append('request', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
      files.forEach((file, index) => {
        const updatedFile = new File([file], file.name, { type: 'image/png', lastModified: file.lastModified });
        formDataToSend.append('images', updatedFile);
      });

      const response = await apiDefinitions.addVehEmissionTest(formDataToSend);
      const data: ApiResponse<EmissionTest> = response.data;
      if (data.status === "S1000" && data.data) {
        showSuccessAlert("Emission test added successfully");
        setEmissionTest(data.data);
        setShowAddModal(false);
        setFiles([]);
        setEmissionImagePreviews([]);
      } else if (data.statusDescription?.toLowerCase().includes("already exists")) {
        showItemExistsError("This emission test already exists.");
      } else {
        showSuccessAlert("Emission test added successfully");
      }
    } catch (err: any) {
      const message = err.response?.data?.statusDescription || err.message || "Error adding emission test";
      if (message.toLowerCase().includes("already exists")) {
        showItemExistsError("This emission test already exists.");
      } else {
        showError(message);
      }
    }
  };

  const downloadEmissionHistory = () => {
    const formattedData = history.map((item) => ({
      NumberPlate: item.numberPlate,
      VehicleClass: item.vehicleClass || "N/A",
      SerialNumber: item.serialNumber || "N/A",
      FuelType: item.fuelType || "N/A",
      GrossWeight: item.grossWeight,
      NoOfKMs: item.noOfKms,
      IssueDate: formatDate(item.issueDate),
      ExpiryDate: formatDate(item.expiryDate),
      Center: item.center || "N/A",
      TestFee: item.testFee,
      OverallStatus: item.overallStatus || "N/A",
      Status: item.status || "N/A",
      CreatedBy: item.createdBy || "N/A",
      CreatedAt: formatDate(item.createdAt),
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'EmissionTestHistory');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const fileData = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    saveAs(fileData, `Emission_Test_History_${numberPlate}.xlsx`);
  };

    // CORRECT: Always define hooks at top level
  const isElectricVehicle = (fuelType: string | null): boolean => {
    return (fuelType || '').toLowerCase() === 'electric';
  };

  // CORRECT: useEffect is now unconditional
  useEffect(() => {
    const fetchEmissionTest = async () => {
      if (!numberPlate) {
        setError("No number plate provided.");
        showError("No number plate provided.");
        setLoading(false);
        return;
      }

      // For Electric vehicles: Show exempt message and skip API call
      if (isElectricVehicle(fuelType)) {
        setEmissionTest(null);
        setError(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await apiDefinitions.getEmissionTest(numberPlate);
        const data: ApiResponse<EmissionTest> = response.data;

        if (data.status === "S1000" && data.data) {
          setEmissionTest(data.data);
        } else if (data.status !== "E1029") {
          setError(data.statusDescription || `Failed to fetch emission test (Status: ${data.status})`);
          showError(data.statusDescription || `Failed to fetch emission test (Status: ${data.status})`);
        }
      } catch (err: any) {
        const message = err.response?.data?.statusDescription || err.message || "Error fetching emission test";
        setError(message);
        showError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchEmissionTest();
  }, [numberPlate, fuelType]); // Add fuelType to deps 


  useEffect(() => {
    generatePreviews(files, setEmissionImagePreviews);
    return () => emissionImagePreviews.forEach(URL.revokeObjectURL);
  }, [files]);

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Emission Test - {numberPlate}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && <div className="text-center">Loading...</div>}
          {error && <div className="alert alert-danger">{error}</div>}
          {!loading && !error && !emissionTest && <div className="alert alert-info">No emission test found.</div>}
          {!loading && !error && emissionTest && (
            <div className="row">
              <div className="col-md-6">
                <h5 className="mb-3">General Information</h5>
                <p><strong>ID:</strong> {emissionTest.id}</p>
                <p><strong>Owner Name:</strong> {emissionTest.ownerName || "N/A"}</p>
                <p><strong>Owner Address:</strong> {emissionTest.ownerAddress || "N/A"}</p>
                <p><strong>Vehicle Class:</strong> {emissionTest.vehicleClass || "N/A"}</p>
                <p><strong>Fuel Type:</strong> {emissionTest.fuelType || "N/A"}</p>
                <p><strong>Gross Weight:</strong> {emissionTest.grossWeight} kg</p>
                <p><strong>No. of KMs:</strong> {emissionTest.noOfKms.toLocaleString()}</p>
                <p><strong>Serial Number:</strong> {emissionTest.serialNumber || "N/A"}</p>
                <p><strong>Center:</strong> {emissionTest.center || "N/A"}</p>
              </div>
              <div className="col-md-6">
                <p><strong>Test Fee:</strong> Rs. {emissionTest.testFee.toLocaleString()}</p>
                <p><strong>Overall Status:</strong> {emissionTest.overallStatus || "N/A"}</p>
                <p><strong>Status:</strong> {emissionTest.status || "N/A"}</p>
                <p><strong>Created By:</strong> {emissionTest.createdBy || "N/A"}</p>
                <p><strong>Created At:</strong> {formatDate(emissionTest.createdAt)}</p>
                <h5 className="mb-3">Dates</h5>
                <p><strong>Issue Date:</strong> {formatDate(emissionTest.issueDate)}</p>
                <p><strong>Expiry Date:</strong> {formatDate(emissionTest.expiryDate)}</p>
                <h5>Images</h5>
                {emissionTest.images && emissionTest.images.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {emissionTest.images.map((image, index) => (
                      <Button
                        key={`emission-image-${index}`}
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleImageClick(image)}
                      >
                        View Image {index + 1}
                      </Button>
                    ))}
                  </div>
                ) : (
                  <p>None</p>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={handleAddModalOpen}>
            Add Emission Test
          </Button>
          <Button variant="info" onClick={handleHistoryModalOpen}>
            View History
          </Button>
          <Button variant="primary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showImageModal} onHide={handleCloseImageModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>View Emission Test Image</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedImage ? (
            <img
              src={selectedImage}
              alt="Emission Test Document"
              style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
              onError={() => showError("Failed to load image.")}
            />
          ) : (
            <p>No image selected.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseImageModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAddModal} onHide={handleAddModalClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Emission Test - {numberPlate}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleFormSubmit}>
            <div className="container">
              <div className="mb-4">
                <h5>Emission Test Records</h5>
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Vehicle Class</label>
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        name="vehicleClass"
                        value={formData.vehicleClass}
                        readOnly
                        placeholder="Vehicle class"
                      />
                      {formErrors.vehicleClass && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.vehicleClass}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Serial Number</label>
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        name="serialNumber"
                        value={formData.serialNumber}
                        onChange={handleFormChange}
                        placeholder="Enter serial number"
                      />
                      {formErrors.serialNumber && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.serialNumber}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Fuel Type</label>
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        name="fuelType"
                        value={formData.fuelType}
                        readOnly
                        placeholder="Fuel type"
                      />
                      {formErrors.fuelType && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.fuelType}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Number of KMs</label>
                      <input
                        type="number"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        name="noOfKms"
                        value={formData.noOfKms}
                        onChange={handleFormChange}
                        placeholder="Enter number of KMs"
                      />
                      {formErrors.noOfKms && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.noOfKms}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Center</label>
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        name="center"
                        value={formData.center}
                        onChange={handleFormChange}
                        placeholder="Enter test center"
                      />
                      {formErrors.center && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.center}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Test Fee</label>
                      <input
                        type="number"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        name="testFee"
                        value={formData.testFee}
                        onChange={handleFormChange}
                        placeholder="Enter test fee"
                        min="0"
                        step="0.01"
                      />
                      {formErrors.testFee && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.testFee}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Overall Status</label>
                      <select
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        name="overallStatus"
                        value={formData.overallStatus}
                        onChange={handleFormChange}
                      >
                        <option value="">Select status</option>
                        <option value="Pass">Pass</option>
                        <option value="Fail">Fail</option>
                      </select>
                      {formErrors.overallStatus && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.overallStatus}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Issue Date</label>
                      <input
                        type="date"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        name="issueDate"
                        value={formData.issueDate}
                        onChange={handleFormChange}
                      />
                      {formErrors.issueDate && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.issueDate}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Expiry Date</label>
                      <input
                        type="date"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        name="expiryDate"
                        value={formData.expiryDate}
                        onChange={handleFormChange}
                      />
                      {formErrors.expiryDate && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.expiryDate}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="mb-0">Emission Test Images (optional)</label>
                      <input
                        type="file"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        multiple
                        accept={allowedTypes.join(',')}
                        onChange={(e) => handleFileChange(e, setFiles, maxFileSizeMB, allowedTypes)}
                      />
                      <small className="text-muted">Max {maxFileSizeMB}MB per file, formats: JPEG, PNG, JPG</small>
                      {formErrors.images && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.images}
                        </div>
                      )}
                      {files.length > 0 && (
                        <div className="mt-3">
                          <h6>Selected Files:</h6>
                          <div className="d-flex flex-wrap gap-2">
                            {files.map((file, index) => (
                              <div key={index} className="position-relative">
                                <img
                                  src={emissionImagePreviews[index]}
                                  alt={file.name}
                                  style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '4px' }}
                                />
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm position-absolute top-0 end-0"
                                  style={{ padding: '2px 6px' }}
                                  onClick={() => removeFile(index, files, setFiles, emissionImagePreviews, setEmissionImagePreviews)}
                                >
                                  ×
                                </button>
                                <div style={{ fontSize: '12px', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {file.name}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <button type="submit" className="btn btn-success">
                Submit
              </button>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleAddModalClose}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showHistoryModal} onHide={handleHistoryModalClose} size="lg">
        <Modal.Header closeButton>
          <div className="d-flex align-items-center w-100">
            <Modal.Title>Emission Test History - {numberPlate}</Modal.Title>
            <div className="ms-auto me-5">
              <Button
                variant="success"
                onClick={downloadEmissionHistory}
                disabled={history.length === 0}
              >
                Download
              </Button>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          {historyLoading && <div className="text-center">Loading...</div>}
          {historyError && <div className="alert alert-danger">{historyError}</div>}
          {!historyLoading && !historyError && history.length === 0 && (
            <div className="alert alert-info">No emission test history found for this vehicle.</div>
          )}
          {!historyLoading && !historyError && history.length > 0 && (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Serial Number</th>
                  <th>Vehicle Class</th>
                  <th>Issue Date</th>
                  <th>Expiry Date</th>
                  <th>Overall Status</th>
                  <th>Images</th>
                </tr>
              </thead>
              <tbody>
                {history.map((record, index) => (
                  <tr key={`history-${index}`}>
                    <td>{record.serialNumber || "N/A"}</td>
                    <td>{record.vehicleClass || "N/A"}</td>
                    <td>{formatDate(record.issueDate)}</td>
                    <td>{formatDate(record.expiryDate)}</td>
                    <td>{record.overallStatus || "N/A"}</td>
                    <td>
                      {record.images && record.images.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2">
                          {record.images.map((image, imgIndex) => (
                            <Button
                              key={`history-image-${index}-${imgIndex}`}
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleImageClick(image)}
                            >
                              View Image {imgIndex + 1}
                            </Button>
                          ))}
                        </div>
                      ) : (
                        "None"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleHistoryModalClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default VehicleEmissionTest;